import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import AuthService from '@/services/AuthService'
import { useNotification } from '@/hooks/useNotification'
import { roleMapping } from '@/const/role-mapping'
import { UserRole } from '@/const/roles'

// Map role string from server to slug
const roleStringToSlug = {
  admin: UserRole.ADMIN,
  'category-specialist': UserRole.CS,
  'category-leader': UserRole.CL,
  manajemen: UserRole.MANAGEMENT,
  management: UserRole.MANAGEMENT,
  dic: UserRole.DIC,
  user: UserRole.USER,
  vendor: UserRole.VENDOR,
}

export const useAuthStore = defineStore(
  'auth-store',
  () => {
    const notification = useNotification()

    const returnUrl = ref(null)
    const lastVisited = ref(null)

    const token = ref('')
    const refreshToken = ref('')
    const user = ref(null)
    const userRole = ref('')

    const userLevel = computed(() => user.value?.level || null)

    const isLoggedIn = computed(() => Boolean(token.value?.trim()))
    const isAuthenticated = computed(() => isLoggedIn.value)

    /** @type {import('.').User | null} */
    const mappedUser = computed(() => {
      if (!user.value) return null

      const roleConfig = roleMapping[userRole.value]

      return {
        name: user.value.name,
        email: user.value.email,
        role: roleConfig?.role ?? 'Unknown',
        navigation: roleConfig?.navigation ?? [],
      }
    })

    /** @type {import('vue').ComputedRef<string[]>} */
    const allowedNavigation = computed(() => {
      return mappedUser.value?.navigation ?? []
    })

    /**
     * Check Access based on route roles
     * @param {number[]} routeRoles
     * @returns {boolean}
     */
    const hasAccess = routeRoles => {
      if (!routeRoles || routeRoles.length === 0) return true
      return routeRoles.includes(userRole.value)
    }

    /**
     * Hapus auth dari state
     */
    const clearAuth = () => {
      token.value = ''
      // refreshToken.value = ''
      user.value = null
      userRole.value = ''
    }

    /**
     * Login
     * @param {string} username
     * @param {string} password
     * @returns {Promise<boolean>}
     */
    const login = async (username, password) => {
      try {
        const data = await AuthService.login({
          username,
          password,
        })

        token.value = data.jwtToken
        // refreshToken.value = data.refreshToken
        user.value = {
          email: data.email,
          role: data.role,
          permissions: data.permissions || [],
          vendor_code: data.vendor_code,
          user_id: data.user_id,
          username: data.username,
          type: data.type,
        }

        // Convert role string to role slug
        const roleSlug = roleStringToSlug[data.role?.toLowerCase()] || UserRole.USER
        userRole.value = roleSlug

        notification.info(`Welcome back ${data?.email ?? ''}`)
        return true
      } catch (error) {
        notification.error(error.response?.data?.message ?? 'Invalid email/password')
        return false
      }
    }

    /**
     * Logout
     */
    const logout = async () => {
      try {
        if (refreshToken.value) {
          await AuthService.logout(refreshToken.value)
        }
      } finally {
        clearAuth()
      }
    }

    return {
      token,
      refreshToken,
      user,
      userRole,
      userLevel,
      returnUrl,
      lastVisited,

      isLoggedIn,
      isAuthenticated,
      mappedUser,
      allowedNavigation,

      hasAccess,
      login,
      logout,
    }
  },
  {
    persist: {
      // returnUrl must survive the full-page navigation of the Google OAuth
      // round trip, otherwise the notification deep link is lost on the way back.
      paths: ['token', 'refreshToken', 'user', 'userRole', 'returnUrl'],
    },
  }
)
